import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Bell,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Info,
  MessageSquare,
  Users,
  Clock,
  Filter,
  Search,
  MoreVertical,
  Trash2,
  Eye,
  CheckCheck,
  Building2,
  X,
  ShieldAlert,
  AtSign,
  HelpCircle,
  RefreshCw,
  BarChart2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { 
  notifyTaskCompleted, 
  notifyTaskDeleted,
} from '../../lib/notificationService';

// Bildirim tiplerini tanımla
const NOTIFICATION_TYPES = [
  { id: 'all', label: 'Tümü', color: 'gray' },
  { id: 'task', label: 'Görev', color: 'blue', icon: CheckCircle2 },
  { id: 'project', label: 'Proje', color: 'purple', icon: Building2 },
  { id: 'team', label: 'Ekip', color: 'green', icon: Users },
  { id: 'mention', label: 'Bahsedilme', color: 'yellow', icon: AtSign },
  { id: 'system', label: 'Sistem', color: 'orange', icon: Info },
  { id: 'security', label: 'Güvenlik', color: 'red', icon: ShieldAlert },
];

// Bildirimler için Icon belirleyen yardımcı fonksiyon
const getNotificationIcon = (type) => {
  const notificationType = NOTIFICATION_TYPES.find(t => t.id === type);
  return notificationType ? notificationType.icon : Bell;
};

// Bildirim zaman formatı için yardımcı fonksiyon
const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return date.toLocaleDateString('tr-TR');
  } else if (days > 0) {
    return `${days} gün önce`;
  } else if (hours > 0) {
    return `${hours} saat önce`;
  } else if (minutes > 0) {
    return `${minutes} dakika önce`;
  } else {
    return 'Az önce';
  }
};

const DEFAULT_TITLE = "İş Takip Otomasyonu"; // Varsayılan sayfa başlığı

// Sekme başlığını güncelleme fonksiyonu
const updateTabTitle = (unreadCount) => {
  if (unreadCount > 0) {
    document.title = `(${unreadCount}) Yeni Bildirim! | ${DEFAULT_TITLE}`;
    // Favicon'u değiştir (🔔)
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) {
      favicon.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23f59e0b'><path d='M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z'/></svg>";
    }
  } else {
    document.title = DEFAULT_TITLE;
    // Normal favicon'a geri dön
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) {
      favicon.href = "/favicon.ico"; // varsayılan favicon yolu
    }
  }
};

const Notifications = () => {
  const navigate = useNavigate();
  
  // State tanımlamaları
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all'); // 'all', 'read', 'unread'
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [notificationStats, setNotificationStats] = useState({
    total: 0,
    unread: 0,
    byType: {},
    byDate: {},
    oldestDate: null,
    newestDate: null,
    averagePerDay: 0
  });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [displayCount, setDisplayCount] = useState(5);
  
  // Tarih bazlı silme özelliği için yeni state'ler
  const [deleteBeforeDate, setDeleteBeforeDate] = useState("");
  const [isDeletingOld, setIsDeletingOld] = useState(false);
  const [showDateDeleteConfirmation, setShowDateDeleteConfirmation] = useState(false);
  const [affectedNotificationsCount, setAffectedNotificationsCount] = useState(0);

  // Arama yapıldığında istatistikleri gizle
  useEffect(() => {
    if (searchQuery.length > 0 && showStats) {
      setShowStats(false);
    }
  }, [searchQuery]);

  // Local storage helpers for system notification states (per-user)
  const getSystemStateKey = (userId, kind) => `system_notifications_${kind}_${userId}`;
  const loadSystemState = (userId, kind) => {
    try {
      const raw = localStorage.getItem(getSystemStateKey(userId, kind));
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch (_) {
      return new Set();
    }
  };
  const saveSystemState = (userId, kind, set) => {
    try {
      localStorage.setItem(getSystemStateKey(userId, kind), JSON.stringify(Array.from(set)));
    } catch (_) {}
  };

  // Mevcut kullanıcıyı al
  const getCurrentUser = useCallback(async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      setCurrentUser(user);
      return user;
    } catch (error) {
      console.error('Error getting current user:', error.message);
      setError('Kullanıcı bilgisi alınamadı');
      return null;
    }
  }, []);

  // Bildirimleri çek
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const user = currentUser || await getCurrentUser();
      
      if (!user) {
        setError('Kullanıcı oturumu bulunamadı');
        setLoading(false);
        return;
      }

      // 1. Kullanıcının kişisel bildirimlerini getir
      const { data: userNotifications, error: userNotificationsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (userNotificationsError) throw userNotificationsError;

      // 2. Sistem bildirimlerini getir (tüm kullanıcılara açık)
      const { data: systemNotifications, error: systemNotificationsError } = await supabase
        .from('system_notifications')
        .select('*')
        .eq('is_active', true) // Sadece aktif bildirimleri göster
        .order('created_at', { ascending: false });

      if (systemNotificationsError) throw systemNotificationsError;

      // Kullanıcıya ait sistem okundu/silinmiş durumlarını yükle
      const readSet = loadSystemState(user.id, 'read');
      const deletedSet = loadSystemState(user.id, 'deleted');

      // Bildirimleri birleştir ve sırala (en yeniden eskiye)
      const allNotifications = [...(userNotifications || []), ...(systemNotifications || [])
        .filter(sysNot => !deletedSet.has(sysNot.id))
        .map(sysNot => ({
          ...sysNot,
          type: 'system',
          user_id: user.id,
          is_read: readSet.has(sysNot.id),
          is_system: true,
          system_id: sysNot.id,
        }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // Bildirim varsa, ilgili kullanıcı profillerini çek (hem kişisel hem sistem bildirimleri için)
      if (allNotifications && allNotifications.length > 0) {
        const creatorIds = [...new Set(
          allNotifications
            .map(notification => notification.created_by)
            .filter(id => id !== null)
        )];

        if (creatorIds.length > 0) {
          const { data: creators, error: creatorsError } = await supabase
            .from('user_profiles')
            .select('id, first_name, last_name, profile_photo_url')
            .in('id', creatorIds);

          if (creatorsError) {
            console.error('Error fetching creator profiles:', creatorsError.message);
          } else {
            const notificationsWithCreators = allNotifications.map(notification => {
              const creator = creators?.find(c => c.id === notification.created_by);
              return {
                ...notification,
                creator
              };
            });
            
            setNotifications(notificationsWithCreators);
            setError(null);
            return;
          }
        }
      }
      
      // Eğer ek kullanıcı verileri alınamazsa veya hiç bildirim yoksa, sadece birleştirilmiş bildirim verilerini kullan
      setNotifications(allNotifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error.message);
      setError('Bildirimler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [currentUser, getCurrentUser]);

  // Filtrelenmiş bildirimleri hesapla
  const filteredNotifications = notifications.filter(notification => {
    const matchesType = typeFilter === 'all' || notification.type === typeFilter;
    const matchesReadStatus = 
      readFilter === 'all' || 
      (readFilter === 'read' && notification.is_read) || 
      (readFilter === 'unread' && !notification.is_read);
    const matchesSearch = 
      searchQuery === '' || 
      notification.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      notification.content.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesType && matchesReadStatus && matchesSearch;
  });

  // Okunmamış bildirim sayısını hesapla
  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Gösterilecek bildirimleri sınırla
  const displayedNotifications = filteredNotifications.slice(0, displayCount);

  // Daha fazla göster fonksiyonu
  const loadMore = () => {
    setDisplayCount(prev => prev + 5);
  };

  // Bildirimi okundu olarak işaretle
  const markAsRead = async (id, opts = {}) => {
    try {
      if (opts.isSystem && currentUser) {
        // Persist locally per user
        const readSet = loadSystemState(currentUser.id, 'read');
        readSet.add(opts.systemId);
        saveSystemState(currentUser.id, 'read', readSet);
      } else {
        const { error } = await supabase
          .from('notifications')
          .update({ is_read: true, updated_at: new Date().toISOString() })
          .eq('id', id);
        if (error) throw error;

        // Log the action in notification_history
        await supabase
          .from('notification_history')
          .insert({
            notification_id: id,
            user_id: currentUser.id,
            action: 'read'
          });
      }

      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(n => 
          n.id === id ? { ...n, is_read: true } : (opts.isSystem && n.system_id === opts.systemId ? { ...n, is_read: true } : n)
        )
      );

      toast.success('Bildirim okundu olarak işaretlendi');
    } catch (error) {
      console.error('Error marking notification as read:', error.message);
      toast.error('Bildirim işaretlenirken bir hata oluştu');
    }
  };

  // Bildirimi okunmadı olarak işaretle
  const markAsUnread = async (id, opts = {}) => {
    try {
      if (opts.isSystem && currentUser) {
        // Persist locally per user
        const readSet = loadSystemState(currentUser.id, 'read');
        readSet.delete(opts.systemId);
        saveSystemState(currentUser.id, 'read', readSet);
      } else {
        const { error } = await supabase
          .from('notifications')
          .update({ is_read: false, updated_at: new Date().toISOString() })
          .eq('id', id);
        if (error) throw error;

        // Note: Not logging 'unread' action in history as it's not in schema constraint
        // The schema only allows 'read', 'deleted', 'restored' actions
      }

      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(n => 
          n.id === id ? { ...n, is_read: false } : (opts.isSystem && n.system_id === opts.systemId ? { ...n, is_read: false } : n)
        )
      );

      toast.success('Bildirim okunmadı olarak işaretlendi');
    } catch (error) {
      console.error('Error marking notification as unread:', error.message);
      toast.error('Bildirim işaretlenirken bir hata oluştu');
    }
  };

  // Tüm bildirimleri okundu olarak işaretle
  const markAllAsRead = async () => {
    try {
      if (!currentUser) return;
      // notifications tablosundakileri güncelle
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('user_id', currentUser.id)
        .eq('is_read', false);

      if (error) throw error;

      // Update notification history for all marked notifications
      const unreadNotifications = notifications.filter(n => !n.is_read);
      
      if (unreadNotifications.length > 0) {
        const historyEntries = unreadNotifications.map(n => ({
          notification_id: n.id,
          user_id: currentUser.id,
          action: 'read'
        }));
        
        await supabase
          .from('notification_history')
          .insert(historyEntries);
      }

      // Sistem bildirimleri için local state güncelle
      const readSet = loadSystemState(currentUser.id, 'read');
      notifications.filter(n => n.is_system).forEach(n => {
        if (n.system_id) readSet.add(n.system_id);
      });
      saveSystemState(currentUser.id, 'read', readSet);

      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(n => ({ ...n, is_read: true }))
      );

      toast.success('Tüm bildirimler okundu olarak işaretlendi');
    } catch (error) {
      console.error('Error marking all notifications as read:', error.message);
      toast.error('Bildirimler işaretlenirken bir hata oluştu');
    }
  };

  // Bildirimi sil
  const deleteNotification = async (id, opts = {}) => {
    try {
      if (opts.isSystem && currentUser) {
        const deletedSet = loadSystemState(currentUser.id, 'deleted');
        deletedSet.add(opts.systemId);
        saveSystemState(currentUser.id, 'deleted', deletedSet);
      } else {
        // Önce bildirim geçmişini sil
        const { error: historyError } = await supabase
          .from('notification_history')
          .delete()
          .eq('notification_id', id);
        if (historyError) throw historyError;

        // Sonra bildirimi sil
        const { error } = await supabase
          .from('notifications')
          .delete()
          .eq('id', id);
        if (error) throw error;
      }

      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.filter(n => n.id !== id && !(opts.isSystem && n.system_id === opts.systemId))
      );

      toast.success('Bildirim silindi');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Bildirim silinirken bir hata oluştu');
    }
  };

  // İlgili sayfaya yönlendir ve bildirimi okundu olarak işaretle
  const handleNotificationClick = async (notification) => {
    try {
      // Bildirimi okundu olarak işaretle
      if (!notification.is_read) {
        if (notification.is_system) {
          await markAsRead(notification.id, { isSystem: true, systemId: notification.system_id });
        } else {
          await markAsRead(notification.id);
        }
      }
      
      // Bildirim tipine göre yönlendirme yap
      switch (notification.type) {
        case 'system':
          // Sistem bildirimleri için herhangi bir yönlendirme yapma
          break;
        case 'team':
          navigate('/team');
          break;
        case 'task':
        case 'project':
        default:
          navigate('/activities');
          break;
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
      toast.error('Bildirim işlenirken bir hata oluştu');
    }
  };

  // Component yüklendiğinde ve kullanıcı değiştiğinde bildirimleri çek
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Gerçek zamanlı güncellemeler için Supabase realtime aboneliği
  useEffect(() => {
    if (!currentUser) return;

    const notificationsSubscription = supabase
      .channel('notifications_page_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${currentUser.id}`
        },
        async (payload) => {
          console.log('Notification change received:', payload);
          
          switch (payload.eventType) {
            case 'INSERT':
              // Yeni bildirimin oluşturan kullanıcı bilgilerini çek
              if (payload.new.created_by) {
                const { data: creator, error } = await supabase
                  .from('user_profiles')
                  .select('id, first_name, last_name, profile_photo_url')
                  .eq('id', payload.new.created_by)
                  .single();
                
                if (!error && creator) {
                  payload.new.creator = creator;
                }
              }
              
              setNotifications(prev => [payload.new, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Bildirim toast'u göster
              if (payload.new.title) {
                toast.success(payload.new.title, {
                  duration: 4000,
                  icon: '🔔'
                });
              }
              break;
            case 'UPDATE':
              setNotifications(prev =>
                prev.map(n => {
                  if (n.id === payload.new.id) {
                    return { ...payload.new, creator: n.creator };
                  }
                  return n;
                })
              );
              break;
            case 'DELETE':
              setNotifications(prev =>
                prev.filter(n => n.id !== payload.old.id)
              );
              setUnreadCount(prev => Math.max(0, prev - 1));
              break;
            default:
              break;
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_notifications',
          filter: 'is_active=eq.true' // Sadece aktif sistem bildirimlerini dinle
        },
        (payload) => {
          console.log('System notification change received:', payload);
          
          switch (payload.eventType) {
            case 'INSERT':
              // Yeni sistem bildirimi eklendi
              const newSystemNotification = {
                ...payload.new,
                is_system: true,
                type: 'system',
                is_read: false,
                system_id: payload.new.id,
              };
              
              setNotifications(prev => [newSystemNotification, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Bildirim toast'u göster
              if (payload.new.title) {
                toast.success(payload.new.title, {
                  duration: 4000,
                  icon: '🔔'
                });
              }
              break;
            case 'UPDATE':
              // Sistem bildirimi güncellendi
              setNotifications(prev =>
                prev.map(n => {
                  if (n.is_system && n.system_id === payload.new.id) {
                    return { 
                      ...n, 
                      ...payload.new, 
                      is_system: true,
                      type: 'system',
                      system_id: payload.new.id,
                    };
                  }
                  return n;
                })
              );
              break;
            case 'DELETE':
              // Sistem bildirimi silindi
              setNotifications(prev =>
                prev.filter(n => !(n.is_system && n.system_id === payload.old.id))
              );
              setUnreadCount(prev => Math.max(0, prev - 1));
              break;
            default:
              break;
          }
        }
      )
      .subscribe();

    // Component unmount olduğunda aboneliği iptal et
    return () => {
      supabase.removeChannel(notificationsSubscription);
    };
  }, [currentUser]);

  // Component mount olduğunda ve unmount olduğunda başlığı yönet
  useEffect(() => {
    // Component mount olduğunda okunmamış bildirimleri kontrol et
    const unreadCount = notifications.filter(n => !n.is_read).length;
    updateTabTitle(unreadCount);

    // Component unmount olduğunda başlığı sıfırla
    return () => {
      document.title = DEFAULT_TITLE;
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = "/favicon.ico";
      }
    };
  }, [notifications]);

  // Bildirimleri yenile
  const refreshNotifications = () => {
    setIsRefreshing(true);
    fetchNotifications();
  };

  // Bildirim ses ayarlarını veritabanından çeken fonksiyon (Artık App.jsx'te)
  // const fetchNotificationSoundPreference = async () => { ... };

  // Ses ayarını değiştirme fonksiyonunu güncelleyelim (Artık App.jsx'te)
  // const toggleSound = async () => { ... };

  // Component mount olduğunda ve kullanıcı değiştiğinde ses ayarını çek (Artık App.jsx'te)
  // useEffect(() => { fetchNotificationSoundPreference(); }, []);

  // notification_preferences tablosundaki değişiklikleri dinle (Artık App.jsx'te)
  // useEffect(() => { ... }, [currentUser, isSoundEnabled]);

  // İstatistikleri hesaplama fonksiyonu
  const calculateStats = useCallback(() => {
    const stats = {
      total: notifications.length,
      unread: notifications.filter(n => !n.is_read).length,
      byType: {},
      byDate: {},
      oldestDate: null,
      newestDate: null,
      averagePerDay: 0
    };

    // Tiplere göre grupla
    notifications.forEach(notification => {
      stats.byType[notification.type] = (stats.byType[notification.type] || 0) + 1;
    });

    // Tarihlere göre grupla
    notifications.forEach(notification => {
      const date = new Date(notification.created_at).toLocaleDateString('tr-TR');
      stats.byDate[date] = (stats.byDate[date] || 0) + 1;
    });

    // En eski ve en yeni tarihleri bul
    if (notifications.length > 0) {
      const dates = notifications.map(n => new Date(n.created_at));
      stats.oldestDate = new Date(Math.min(...dates));
      stats.newestDate = new Date(Math.max(...dates));

      // Günlük ortalama hesapla
      const dayDiff = Math.ceil((stats.newestDate - stats.oldestDate) / (1000 * 60 * 60 * 24)) || 1;
      stats.averagePerDay = (stats.total / dayDiff).toFixed(1);
    }

    setNotificationStats(stats);
  }, [notifications]);

  // İstatistikleri güncelle
  useEffect(() => {
    calculateStats();
  }, [notifications, calculateStats]);

  // Tarih öncesi bildirimleri silme fonksiyonu
  const deleteNotificationsBeforeDate = async () => {
    try {
      if (!currentUser || !deleteBeforeDate) return;
      
      setIsDeletingOld(true);
      
      const selectedDate = new Date(deleteBeforeDate);
      selectedDate.setHours(23, 59, 59, 999); // Günün sonuna ayarla
      
      // Silinecek bildirimlerin ID'lerini bul
      const notificationsToDelete = notifications.filter(
        notification => new Date(notification.created_at) < selectedDate
      );
      
      if (notificationsToDelete.length === 0) {
        toast('Oops! Seçtiğiniz tarih öncesine dair bir bildirim bulamadık.');
        setIsDeletingOld(false);
        return;
      }
      
      const notificationIds = notificationsToDelete.map(n => n.id);
      
      // Önce bildirim geçmişini sil
      const { error: historyError } = await supabase
        .from('notification_history')
        .delete()
        .in('notification_id', notificationIds);

      if (historyError) throw historyError;

      // Sonra bildirimleri sil
      const { error } = await supabase
        .from('notifications')
        .delete()
        .in('id', notificationIds);

      if (error) throw error;

      // State'i güncelle
      setNotifications(prev => prev.filter(n => !notificationIds.includes(n.id)));
      toast.success(`${notificationsToDelete.length} bildirim başarıyla silindi`);
      
      // Tarih seçimini sıfırla
      setDeleteBeforeDate("");
    } catch (error) {
      console.error('Error deleting old notifications:', error);
      toast.error('Bildirimler silinirken bir hata oluştu');
    } finally {
      setIsDeletingOld(false);
      setShowDateDeleteConfirmation(false);
    }
  };

  // Tüm bildirimleri sil (kişisel olanları DB'den, sistem olanları yerelde gizle)
  const deleteAllNotifications = async () => {
    try {
      if (!currentUser) return;
      setIsDeletingAll(true);

      const personalIds = notifications.filter(n => !n.is_system).map(n => n.id);

      if (personalIds.length > 0) {
        // Önce bildirim geçmişlerini sil
        const { error: historyError } = await supabase
          .from('notification_history')
          .delete()
          .in('notification_id', personalIds);
        if (historyError) throw historyError;

        // Sonra bildirimleri sil
        const { error: notifError } = await supabase
          .from('notifications')
          .delete()
          .in('id', personalIds);
        if (notifError) throw notifError;
      }

      // Sistem bildirimlerini yerelde silinmiş olarak işaretle
      const deletedSet = loadSystemState(currentUser.id, 'deleted');
      notifications.filter(n => n.is_system && n.system_id).forEach(n => {
        deletedSet.add(n.system_id);
      });
      saveSystemState(currentUser.id, 'deleted', deletedSet);

      // State'i temizle
      setNotifications([]);
      toast.success('Tüm bildirimler silindi');
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      toast.error('Tüm bildirimler silinirken bir hata oluştu');
    } finally {
      setIsDeletingAll(false);
    }
  };
  
  // Silinecek bildirimlerin sayısını hesaplama
  const calculateAffectedNotifications = () => {
    if (!deleteBeforeDate) {
      setAffectedNotificationsCount(0);
      return;
    }
    
    const selectedDate = new Date(deleteBeforeDate);
    selectedDate.setHours(23, 59, 59, 999);
    
    const count = notifications.filter(
      notification => new Date(notification.created_at) < selectedDate
    ).length;
    
    setAffectedNotificationsCount(count);
  };
  
  // Tarih değiştiğinde etkilenen bildirim sayısını güncelle
  useEffect(() => {
    calculateAffectedNotifications();
  }, [deleteBeforeDate, notifications]);

  // İstatistik Kartı Bileşeni
  const StatsCard = () => (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{
        type: "spring",
        stiffness: 100,
        damping: 20,
        opacity: { duration: 0.2 }
      }}
      className="mt-4 overflow-hidden"
    >
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          delay: 0.1
        }}
        className="p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
      >
        <div className="space-y-6">
          {/* Genel İstatistikler */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Toplam Bildirim</h4>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">{notificationStats.total}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Okunmamış</h4>
              <p className="text-2xl font-semibold text-primary-600 dark:text-primary-400 mt-1">{notificationStats.unread}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Günlük Ortalama</h4>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">{notificationStats.averagePerDay}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Tarih Aralığı</h4>
              <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                {notificationStats.oldestDate ? (
                  <>
                    {notificationStats.oldestDate.toLocaleDateString('tr-TR')} -
                    {notificationStats.newestDate.toLocaleDateString('tr-TR')}
                  </>
                ) : (
                  'Bildirim yok'
                )}
              </p>
            </div>
          </motion.div>

          {/* Bildirim Tipleri Dağılımı */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Bildirim Tipleri Dağılımı</h4>
            <div className="space-y-3">
              {Object.entries(notificationStats.byType).map(([type, count], index) => {
                const notificationType = NOTIFICATION_TYPES.find(t => t.id === type) || NOTIFICATION_TYPES[0];
                const percentage = (count / notificationStats.total * 100).toFixed(1);
                
                return (
                  <motion.div
                    key={type}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{
                      delay: 0.4 + (index * 0.1),
                      type: "spring",
                      stiffness: 100,
                      damping: 15
                    }}
                    className="space-y-2"
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <notificationType.icon className={`w-4 h-4 text-${notificationType.color}-500`} />
                        {notificationType.label}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{
                          delay: 0.5 + (index * 0.1),
                          type: "spring",
                          stiffness: 50,
                          damping: 15
                        }}
                        className={`h-full bg-${notificationType.color}-500 rounded-full`}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Günlük Dağılım */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Günlük Dağılım</h4>
            <div className="space-y-2">
              {Object.entries(notificationStats.byDate)
                .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                .map(([date, count], index) => {
                  const percentage = (count / notificationStats.total * 100).toFixed(1);
                  
                  return (
                    <motion.div
                      key={date}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{
                        delay: 0.5 + (index * 0.1),
                        type: "spring",
                        stiffness: 100,
                        damping: 15
                      }}
                      className="flex items-center gap-4"
                    >
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[100px]">{date}</span>
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700/50 rounded-lg overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{
                            delay: 0.6 + (index * 0.1),
                            type: "spring",
                            stiffness: 50,
                            damping: 15
                          }}
                          className="h-full bg-primary-500/20 dark:bg-primary-500/40 flex items-center px-3"
                        >
                          <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
                            {count} bildirim
                          </span>
                        </motion.div>
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          </motion.div>
          
          {/* Tarih bazlı bildirim silme bölümü */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="pt-6 border-t border-gray-200 dark:border-gray-700"
          >
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Eski Bildirimleri Temizle</h4>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-900/30">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    Belirli bir tarihten önceki tüm bildirimleri silebilirsiniz. Bu işlem geri alınamaz.
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Calendar className="w-4 h-4 text-yellow-700 dark:text-yellow-400" />
                    <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                      Seçilen tarihten önceki bildirimler: 
                      <span className="ml-1 font-bold">{affectedNotificationsCount}</span>
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-3 w-full sm:w-auto">
                  <input
                    type="date"
                    value={deleteBeforeDate}
                    onChange={(e) => setDeleteBeforeDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="px-3 py-2 border border-yellow-300 dark:border-yellow-800 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent w-full sm:w-auto"
                  />
                  <button
                    onClick={() => {
                      if (!deleteBeforeDate) {
                        toast.error('Lütfen bir tarih seçin');
                        return;
                      }
                      
                      if (affectedNotificationsCount === 0) {
                        toast('Oops! Seçtiğiniz tarih öncesine dair bir bildirim bulamadık.');
                        return;
                      }
                      
                      setShowDateDeleteConfirmation(true);
                    }}
                    disabled={!deleteBeforeDate || isDeletingOld}
                    className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                  >
                    {isDeletingOld ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        İşleniyor...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Eski Bildirimleri Sil
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );

  // Bildirim Kartı Bileşeni
  const NotificationCard = ({ notification }) => {
    const NotificationIcon = getNotificationIcon(notification.type);
    const notificationType = NOTIFICATION_TYPES.find(t => t.id === notification.type) || NOTIFICATION_TYPES[0];
    const [isUnread, setIsUnread] = useState(!notification.is_read);

    const handleMarkAsRead = async (e) => {
      try {
        e.stopPropagation();
        if (notification.is_system) {
          await markAsRead(notification.id, { isSystem: true, systemId: notification.system_id });
        } else {
          await markAsRead(notification.id);
        }
        setIsUnread(false);
        setShowActionMenu(null);
        toast.success('Bildirim okundu olarak işaretlendi');
      } catch (error) {
        console.error('Error marking as read:', error);
        toast.error('Bildirim işaretlenirken bir hata oluştu');
      }
    };

    const handleMarkAsUnread = async (e) => {
      try {
        e.stopPropagation();
        if (notification.is_system) {
          await markAsUnread(notification.id, { isSystem: true, systemId: notification.system_id });
        } else {
          await markAsUnread(notification.id);
        }
        setIsUnread(true);
        setShowActionMenu(null);
        toast.success('Bildirim okunmadı olarak işaretlendi');
      } catch (error) {
        console.error('Error marking as unread:', error);
        toast.error('Bildirim işaretlenirken bir hata oluştu');
      }
    };

    return (
      <motion.div
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        layout
        className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-all ${
          isUnread
            ? `bg-${notificationType.color}-50/80 dark:bg-${notificationType.color}-900/20 border-${notificationType.color}-200 dark:border-${notificationType.color}-800/30`
            : 'bg-white/90 dark:bg-gray-800/90 border-gray-100 dark:border-gray-700/50'
        }`}
        onClick={() => handleNotificationClick(notification)}
      >
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-${notificationType.color}-100/90 dark:bg-${notificationType.color}-900/40 flex items-center justify-center`}>
            <NotificationIcon className={`w-5 h-5 text-${notificationType.color}-700 dark:text-${notificationType.color}-300`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className={`text-sm font-medium ${
                  isUnread ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'
                }`}>
                  {notification.title}
                </h3>
                {notification.is_system ? (
                  <div className="prose dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 mt-1" dangerouslySetInnerHTML={{ __html: notification.content }} />
                ) : (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {notification.content}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {formatTime(notification.created_at)}
                </span>
                <div className="relative">
                    <button 
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActionMenu(showActionMenu === notification.id ? null : notification.id);
                      }}
                    >
                      <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                    {showActionMenu === notification.id && (
                      <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                        <div className="py-1">
                          {isUnread ? (
                            <button
                              onClick={handleMarkAsRead}
                              className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              Okundu olarak işaretle
                            </button>
                          ) : (
                            <button
                              onClick={handleMarkAsUnread}
                              className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              Okunmadı olarak işaretle
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (notification.is_system) {
                                deleteNotification(notification.id, { isSystem: true, systemId: notification.system_id });
                              } else {
                                deleteNotification(notification.id);
                              }
                              setShowActionMenu(null);
                            }}
                            className="w-full px-4 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Sil
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                
              </div>
            </div>
            
            {notification.creator && (
              <div className="mt-2 flex items-center gap-2">
                <img
                  src={notification.creator.profile_photo_url || `https://ui-avatars.com/api/?name=${notification.creator.first_name}+${notification.creator.last_name}&background=3b82f6&color=fff`}
                  alt={`${notification.creator.first_name} ${notification.creator.last_name}`}
                  className="w-6 h-6 rounded-full"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {notification.creator.first_name} {notification.creator.last_name}
                </span>
              </div>
            )}
            
            {notification.link && !notification.is_system && (
              <div className="mt-3">
                <span className={`text-sm font-medium flex items-center gap-1 ${
                  isUnread 
                    ? 'text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {isUnread ? (
                    <>
                      Okundu olarak işaretle
                      <Eye className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Okundu
                      <CheckCheck className="w-4 h-4" />
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  // Silme Onay Pop-up'ı Bileşeni
  const DeleteConfirmationModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Tüm Bildirimleri Sil
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Bu işlem geri alınamaz. Tüm bildirimleriniz kalıcı olarak silinecektir.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => setShowDeleteConfirmation(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            İptal
          </button>
          <button
            onClick={() => {
              deleteAllNotifications();
              setShowDeleteConfirmation(false);
            }}
            disabled={isDeletingAll}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeletingAll ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Siliniyor...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Tümünü Sil
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  // Tarih bazlı silme onay modalı
  const DateDeleteConfirmationModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Eski Bildirimleri Sil
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              <span className="font-medium text-yellow-600 dark:text-yellow-400">
                {new Date(deleteBeforeDate).toLocaleDateString('tr-TR')}
              </span> tarihinden önceki tüm bildirimler silinecektir.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {affectedNotificationsCount > 0 ? (
                <span>Bu işlem <span className="font-medium text-red-600 dark:text-red-400">{affectedNotificationsCount} bildirim</span> etkileyecek ve geri alınamaz.</span>
              ) : (
                <span>Bu tarihten önce silinecek bildirim bulunmuyor.</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => setShowDateDeleteConfirmation(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            İptal
          </button>
          <button
            onClick={deleteNotificationsBeforeDate}
            disabled={isDeletingOld || affectedNotificationsCount === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeletingOld ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                İşleniyor...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Bildirimleri Sil
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Silme Onay Pop-up'ı */}
      <AnimatePresence>
        {showDeleteConfirmation && <DeleteConfirmationModal />}
        {showDateDeleteConfirmation && <DateDeleteConfirmationModal />}
      </AnimatePresence>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        {/* Başlık ve Araçlar */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col gap-4">
            {/* Üst Satır: Başlık ve Arama/Filtre */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Bildirimler
              </h2>
              <div className="flex items-center gap-2">
                                  <Link
                      to="/activities"
                      className="text-sm mr-4 font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 flex items-center gap-2"
                    >
                      <Calendar className="w-4 h-4" />
                      Görev Arşivi
                    </Link>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Bildirim ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full sm:w-64 pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
                
                <button
                  onClick={refreshNotifications}
                  disabled={isRefreshing}
                  className={`p-2 rounded-lg transition-colors ${
                    isRefreshing
                      ? 'text-primary-500 animate-spin'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
                
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2 rounded-lg transition-colors ${
                    showFilters
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Filter className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Alt Satır: Bildirim Sayısı ve Aksiyonlar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {unreadCount > 0 && (
                  <span className="px-3 py-1.5 text-sm font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    {unreadCount} yeni bildirim
                  </span>
                )}
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-2"
                  >
                    <CheckCheck className="w-4 h-4" />
                    Tümünü okundu işaretle
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {notifications.length > 0 && (
                  <>
                    <button
                      onClick={() => setShowDeleteConfirmation(true)}
                      className="text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex items-center gap-2 mr-4"
                      disabled={isDeletingAll}
                    >
                      <Trash2 className="w-4 h-4" />
                      Tümünü Sil
                    </button>

                    <button
                      onClick={() => setShowStats(!showStats)}
                      className="text-sm font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 flex items-center gap-2"
                    >
                      <BarChart2 className="w-4 h-4" />
                      {showStats ? (
                        <>
                          İstatistikleri Gizle
                          <ChevronUp className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          İstatistikler
                          <ChevronDown className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* İstatistikler */}
          <AnimatePresence mode="popLayout">
            {showStats && notifications.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  opacity: { duration: 0.2 }
                }}
                className="overflow-hidden"
              >
                <StatsCard />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Filtreler */}
          <AnimatePresence mode="popLayout">
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  opacity: { duration: 0.2 }
                }}
                className={`overflow-hidden ${showStats ? 'mt-6' : 'mt-4'}`}
              >
                <motion.div
                  initial={{ y: -20 }}
                  animate={{ y: 0 }}
                  exit={{ y: -20 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30
                  }}
                  className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-4"
                >
                  {/* Bildirim Tip Filtreleri */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Bildirim Tipi</h4>
                    <div className="flex flex-wrap gap-2">
                      {NOTIFICATION_TYPES.map((type) => {
                        const TypeIcon = type.icon || Bell;
                        return (
                          <button
                            key={type.id}
                            onClick={() => setTypeFilter(type.id)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                              typeFilter === type.id
                                ? `bg-${type.color}-100 dark:bg-${type.color}-900/30 text-${type.color}-700 dark:text-${type.color}-400`
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            <TypeIcon className="w-4 h-4" />
                            {type.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Okunma Durumu Filtreleri */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Okunma Durumu</h4>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setReadFilter('all')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                          readFilter === 'all'
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Bell className="w-4 h-4" />
                        Tümü
                      </button>
                      <button
                        onClick={() => setReadFilter('read')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                          readFilter === 'read'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <CheckCheck className="w-4 h-4" />
                        Okunmuş
                      </button>
                      <button
                        onClick={() => setReadFilter('unread')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                          readFilter === 'unread'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Eye className="w-4 h-4" />
                        Okunmamış
                      </button>
                    </div>
                  </div>
                  
                  {/* Filtre Sonuçları ve Temizle */}
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-600 flex justify-between items-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {filteredNotifications.length} bildirim bulundu
                    </p>
                    <button
                      onClick={() => {
                        setTypeFilter('all');
                        setReadFilter('all');
                        setSearchQuery('');
                      }}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                    >
                      Filtreleri Temizle
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Bildirim Listesi */}
        <motion.div 
          className="p-6"
          layout
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
        >
          {loading ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center items-center h-64"
            >
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </motion.div>
          ) : error ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="text-center py-12"
            >
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-red-500 mb-2">
                {error}
              </h3>
              <button
                onClick={refreshNotifications}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Yeniden Dene
              </button>
            </motion.div>
          ) : filteredNotifications.length > 0 ? (
            <motion.div 
              className="space-y-4"
              layout
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
            >
              <AnimatePresence mode="popLayout">
                {displayedNotifications.map((notification, index) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ 
                      opacity: 1, 
                      y: 0,
                      transition: {
                        delay: index * 0.05,
                        type: "spring",
                        stiffness: 300,
                        damping: 30
                      }
                    }}
                    exit={{ 
                      opacity: 0, 
                      y: -20,
                      transition: {
                        duration: 0.2
                      }
                    }}
                    layout
                  >
                    <NotificationCard notification={notification} />
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Daha Fazla Göster Butonu */}
              {displayCount < filteredNotifications.length && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-center mt-6"
                >
                  <button
                    onClick={loadMore}
                    className="px-6 py-3 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 bg-primary-50 dark:bg-primary-900/10 hover:bg-primary-100 dark:hover:bg-primary-900/20 rounded-xl transition-all duration-200 flex items-center gap-2"
                  >
                    <ChevronDown className="w-4 h-4" />
                    Daha Fazla Göster ({filteredNotifications.length - displayCount} bildirim)
                  </button>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
              className="text-center py-12"
            >
              {searchQuery || typeFilter !== 'all' || readFilter !== 'all' ? (
                <>
                  <HelpCircle className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 animate-pulse">
                    Filtrelere Uygun Bildirim Bulunamadı
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Filtreleri değiştirerek veya temizleyerek daha fazla bildirim görebilirsiniz.
                  </p>
                  <button
                    onClick={() => {
                      setTypeFilter('all');
                      setReadFilter('all');
                      setSearchQuery('');
                    }}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Filtreleri Temizle
                  </button>
                </>
              ) : (
                <>
                  <Bell className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4 animate-pulse" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 animate-pulse">
                    Bildiriminiz Bulunmuyor
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 animate-pulse">
                    Henüz herhangi bir bildiriminiz yok. Yeni görevler, projeler veya ekip güncellemeleri olduğunda burada görünecekler.
                  </p>
                </>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Notifications; 